import { IDentalData } from '@dataTypes/chartingFeature.types'
import {
  CUSP_RELATED_SURFACES,
  INCISOR_CANINE_TOOTH_IDS,
  LOWER_INCISOR_CANINE_TOOTH_IDS,
  formatDentalCondition,
} from '@constants/Charting'
import { sortAtomicSurfaces } from '@utils/procedure-surface-selection.utils'

const CHARTING_SURFACE_TO_COMPACT: Record<string, string> = {
  m: 'M',
  d: 'D',
  o: 'O',
  i: 'I',
  b: 'B',
  p: 'P',
  l: 'L',
  cb: 'CB',
  cp: 'CP',
  mb: 'MOB',
  db: 'DOB',
  mp: 'MOP',
  dp: 'DOP',
  bc: 'BC',
  bs: 'BS',
  pc: 'PC',
  ps: 'PS',
  ls: 'LS',
  lc: 'LC',
  cl: 'CL',
  dlc: 'DOL',
  mlc: 'MOL',
  mifp: 'MIFP',
  difp: 'DIFP',
  mifl: 'MIFL',
  difl: 'DIFL',
  buccal: 'B',
  palatal: 'P',
  lingual: 'L',
  'buccal-distal': 'BD',
  'buccal-mesial': 'BM',
}

/** Posterior cusp codes that are not selectable on anteriors (often auto-implied). */
const ANTERIOR_EXCLUDED_SURFACE_CODES = new Set([
  'mb',
  'db',
  'mp',
  'dp',
  'bc',
  'pc',
  'mlc',
  'dlc',
  'lc',
  'o',
])

/** Side panel uses F/CF on anteriors; storage still uses buccal codes (b/cb). */
const ANTERIOR_FACIAL_COMPACT_MAP: Record<string, string> = {
  B: 'F',
  CB: 'CF',
  MB: 'MF',
  DB: 'DF',
  MOB: 'MOF',
  DOB: 'DOF',
  BC: 'FC',
  BS: 'FS',
  BM: 'FM',
  BD: 'FD',
}

const COMPOUND_DISPLAY_ORDER = [
  'M',
  'O',
  'D',
  'B',
  'L',
  'I',
  'F',
  'P',
  'MIFL',
  'DIFL',
  'MIFP',
  'DIFP',
  'MOB',
  'MOF',
  'DOB',
  'DOF',
  'MOP',
  'DOP',
  'MOL',
  'DOL',
  'MB',
  'MF',
  'DB',
  'DF',
  'MP',
  'DP',
  'BC',
  'FC',
  'BS',
  'FS',
  'PC',
  'PS',
  'CB',
  'CF',
  'CP',
  'LC',
  'LS',
  'CL',
  'DLC',
  'MLC',
  'BD',
  'FD',
  'BM',
  'FM',
] as const

export const isAnteriorIncisorOrCanine = (toothNumber: number): boolean => {
  return (
    INCISOR_CANINE_TOOTH_IDS.includes(toothNumber) ||
    LOWER_INCISOR_CANINE_TOOTH_IDS.includes(toothNumber)
  )
}

const chartingTokenToCompact = (
  token: string,
  toothNumber?: number,
): string => {
  const trimmed = token.trim()
  if (!trimmed) {
    return ''
  }
  const lower = trimmed.toLowerCase()
  if (
    toothNumber !== undefined &&
    isAnteriorIncisorOrCanine(toothNumber) &&
    ANTERIOR_EXCLUDED_SURFACE_CODES.has(lower)
  ) {
    return ''
  }
  if (CHARTING_SURFACE_TO_COMPACT[lower]) {
    const compact = CHARTING_SURFACE_TO_COMPACT[lower]
    if (
      toothNumber !== undefined &&
      isAnteriorIncisorOrCanine(toothNumber) &&
      ANTERIOR_FACIAL_COMPACT_MAP[compact]
    ) {
      return ANTERIOR_FACIAL_COMPACT_MAP[compact]
    }
    return compact
  }
  if (lower.includes('-')) {
    return lower
      .split('-')
      .map((segment) => chartingTokenToCompact(segment, toothNumber))
      .filter(Boolean)
      .join('')
  }
  const compact = trimmed.toUpperCase()
  if (
    toothNumber !== undefined &&
    isAnteriorIncisorOrCanine(toothNumber) &&
    ANTERIOR_FACIAL_COMPACT_MAP[compact]
  ) {
    return ANTERIOR_FACIAL_COMPACT_MAP[compact]
  }
  return compact
}

export const parseChartingSurfaceTokens = (
  surface: string | null | undefined,
): string[] => {
  if (!surface?.trim()) {
    return []
  }
  return surface
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
}

/** Drop atomics covered by a present compound cusp (MOB → hide M/O/B). Display only. */
export const omitAtomicsCoveredByCompounds = (
  tokens: readonly string[],
): string[] => {
  const normalized = tokens
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
  if (normalized.length === 0) {
    return []
  }
  const tokenSet = new Set(normalized)
  const coveredAtomics = new Set<string>()
  Object.entries(CUSP_RELATED_SURFACES).forEach(([cusp, related]) => {
    if (!tokenSet.has(cusp)) {
      return
    }
    related.forEach((atomic) => {
      coveredAtomics.add(atomic)
    })
  })
  if (coveredAtomics.size === 0) {
    return [...normalized]
  }
  return normalized.filter((token) => !coveredAtomics.has(token))
}

const sortChartingCompactParts = (parts: string[]): string[] => {
  return [...parts].sort((a, b) => {
    const indexA = COMPOUND_DISPLAY_ORDER.indexOf(
      a as (typeof COMPOUND_DISPLAY_ORDER)[number],
    )
    const indexB = COMPOUND_DISPLAY_ORDER.indexOf(
      b as (typeof COMPOUND_DISPLAY_ORDER)[number],
    )
    const orderA = indexA === -1 ? COMPOUND_DISPLAY_ORDER.length : indexA
    const orderB = indexB === -1 ? COMPOUND_DISPLAY_ORDER.length : indexB
    if (orderA !== orderB) {
      return orderA - orderB
    }
    return a.localeCompare(b)
  })
}

export const chartingTokensToAbbrevParts = (
  tokens: string[],
  toothNumber?: number,
): string[] => {
  const displayTokens = omitAtomicsCoveredByCompounds(tokens)
  const atomicParts: string[] = []
  const compoundParts: string[] = []
  const seen = new Set<string>()
  for (const token of displayTokens) {
    const compact = chartingTokenToCompact(token, toothNumber)
    if (!compact || seen.has(compact)) {
      continue
    }
    seen.add(compact)
    if (compact.length === 1) {
      atomicParts.push(compact)
    } else {
      compoundParts.push(compact)
    }
  }
  return [
    ...sortAtomicSurfaces(atomicParts),
    ...sortChartingCompactParts(compoundParts),
  ]
}

export const chartingTokensToCompactAbbreviations = (
  tokens: string[],
  toothNumber?: number,
): string => {
  return chartingTokensToAbbrevParts(tokens, toothNumber).join('')
}

export const formatChartingSurfaceCompact = (
  surface: string | null | undefined,
  toothNumber?: number,
): string => {
  return chartingTokensToCompactAbbreviations(
    parseChartingSurfaceTokens(surface),
    toothNumber,
  )
}

export const formatChartingToothWithSurfaces = (
  tooth: string | number,
  surface: string | null | undefined,
  isFurcation: boolean,
): string => {
  if (isFurcation) {
    return String(tooth)
  }
  const toothNumber =
    typeof tooth === 'number' ? tooth : parseInt(String(tooth), 10)
  const abbrevParts = chartingTokensToAbbrevParts(
    parseChartingSurfaceTokens(surface),
    Number.isNaN(toothNumber) ? undefined : toothNumber,
  )
  if (abbrevParts.length === 0) {
    return String(tooth)
  }
  return `${tooth}(${abbrevParts.join(',')})`
}

const getDentalDataToothNumber = (entry: IDentalData): number | null => {
  const toothNum =
    typeof entry.tooth_number === 'string'
      ? parseInt(entry.tooth_number, 10)
      : entry.tooth_number
  return Number.isNaN(toothNum) ? null : toothNum
}

type ToothSurfaceDisplayEntry = {
  toothNumber: number
  compactSurfaces: string
}

const formatToothSurfaceRange = (
  firstTooth: number,
  lastTooth: number,
  compactSurfaces: string,
): string => {
  const toothLabel =
    firstTooth === lastTooth ? String(firstTooth) : `${firstTooth}–${lastTooth}`
  return compactSurfaces ? `${toothLabel}(${compactSurfaces})` : toothLabel
}

export const groupDentalDataToothSurfaceEntries = (
  dentalData: IDentalData[] | undefined | null,
): string[] => {
  if (!dentalData || dentalData.length === 0) {
    return []
  }
  const tokensByTooth = new Map<number, string[]>()
  const furcationTeeth = new Set<number>()
  for (const item of dentalData) {
    const toothNum = getDentalDataToothNumber(item)
    if (toothNum === null) {
      continue
    }
    const isFurcation = item.condition?.toLowerCase() === 'furcation'
    if (isFurcation) {
      furcationTeeth.add(toothNum)
      if (!tokensByTooth.has(toothNum)) {
        tokensByTooth.set(toothNum, [])
      }
      continue
    }
    const existing = tokensByTooth.get(toothNum) ?? []
    existing.push(...parseChartingSurfaceTokens(item.surface))
    tokensByTooth.set(toothNum, existing)
  }
  const entries = Array.from(tokensByTooth.entries())
    .sort(([a], [b]) => a - b)
    .map(([toothNumber, tokens]): ToothSurfaceDisplayEntry => {
      const compactSurfaces =
        furcationTeeth.has(toothNumber) && tokens.length === 0
          ? ''
          : chartingTokensToCompactAbbreviations(tokens, toothNumber)
      return { toothNumber, compactSurfaces }
    })

  if (entries.length === 0) {
    return []
  }

  const groupedEntries: string[] = []
  let rangeStart = entries[0]
  let rangeEnd = entries[0]

  for (let index = 1; index < entries.length; index += 1) {
    const entry = entries[index]
    const continuesRange =
      entry.toothNumber === rangeEnd.toothNumber + 1 &&
      entry.compactSurfaces === rangeStart.compactSurfaces

    if (continuesRange) {
      rangeEnd = entry
      continue
    }

    groupedEntries.push(
      formatToothSurfaceRange(
        rangeStart.toothNumber,
        rangeEnd.toothNumber,
        rangeStart.compactSurfaces,
      ),
    )
    rangeStart = entry
    rangeEnd = entry
  }

  groupedEntries.push(
    formatToothSurfaceRange(
      rangeStart.toothNumber,
      rangeEnd.toothNumber,
      rangeStart.compactSurfaces,
    ),
  )
  return groupedEntries
}

export const groupDentalDataToothSurfaceColumn = (
  dentalData: IDentalData[] | undefined | null,
): string => {
  return groupDentalDataToothSurfaceEntries(dentalData).join(', ')
}

/** One condition label per tooth; dedupe identical labels on the same tooth (e.g. 11/MID → one Caries). */
export const groupDentalDataToothConditionColumn = (
  dentalData: IDentalData[] | undefined | null,
): string => {
  if (!dentalData || dentalData.length === 0) {
    return ''
  }
  const conditionsByTooth = new Map<number, string[]>()
  for (const item of dentalData) {
    const toothNum = getDentalDataToothNumber(item)
    if (toothNum === null) {
      continue
    }
    const conditionDisplay = formatDentalCondition(
      item.condition,
      item.type,
      item.material,
      null,
      item.recurrent,
    )
    if (!conditionDisplay) {
      continue
    }
    const existing = conditionsByTooth.get(toothNum) ?? []
    if (!existing.includes(conditionDisplay)) {
      existing.push(conditionDisplay)
    }
    conditionsByTooth.set(toothNum, existing)
  }
  return Array.from(conditionsByTooth.entries())
    .sort(([a], [b]) => a - b)
    .flatMap(([, conditions]) => conditions)
    .join(', ')
}
